import { useEffect, useRef, useState } from "react";
import { ReactTyped } from "react-typed";
import { IAIMessage, IMessageBtn, IPortList } from "types/chat";
import ReactMarkdown from "react-markdown";
import CustomButton from "components/common/CustomButton";
import { formAxios, jsonAxios } from "components/axios";
import { IContainerList, IQuote } from "types/quote";
import HsCode from "components/common/HsCode";
import HsCodeModal from "components/common/modal/HsCodeModal";
import { hsCodePopAtom, hsCodeResultAtom } from "atom/hsCode";
import { useAtom, useSetAtom } from "jotai";
import DragFileMulti from "components/common/DragFileMulti";
import { toast } from "react-toastify";
import { sendTeamRoom } from "components/nateon";
import { countryCodeToEmoji, getKoreaDateTime } from "components/function";
import { useGetUser } from "atom/user";
import { useNavigate } from "react-router-dom";
import { isPositiveInteger, isValidDateFormat } from "components/common/regex";
import ConfrimChatQuote from "components/common/modal/ConfrimChatQuote";

interface IQuoteDetail {
    chatId: number;
    setChatId: React.Dispatch<React.SetStateAction<number>>,
    getChatbotList: () => void
}

const Quote = ({ chatId, setChatId, getChatbotList }: IQuoteDetail) => {
    const user = useGetUser();
    const navigate = useNavigate();
    const [context, setContext] = useState<string>('');
    const [messages, setMessages] = useState<IAIMessage[]>([]);
    const chatRef = useRef<HTMLDivElement>(null);
    const lastBotMessageRef = useRef<HTMLDivElement | null>(null);
    const [step, setStep] = useState<number>(0);
    const [quoteInfo, setQuoteInfo] = useState<IQuote>({} as IQuote)
    const [popHsCodeOpen, setPoHsCodepOpen] = useAtom(hsCodePopAtom)
    const [fileArr, setFileArr] = useState<File[]>([]);
    const [once, setOnce] = useState<boolean>(false)
    const [isDone, setIsDone] = useState<boolean>(false)
    const [containerInfo, setContainerInfo] = useState<IContainerList>({} as IContainerList)
    const setHsCodeResult = useSetAtom(hsCodeResultAtom);
    const [quoteId, setQuoteId] = useState<number>(-1)
    const [isDelete, setIsDelete] = useState<boolean>(false)
    const [popOpen, setPopOpen] = useState<boolean>(false)

    useEffect(() => {
        setHsCodeResult('');
        setIsDelete(false)
        setIsDone(false)
        setQuoteId(-1)
        setStep(0)
        setFileArr([])
        setContainerInfo({} as IContainerList)
        if (chatId > 0) {
            init();
        }
        else {
            start()
        }
    }, [chatId])

    const stepInfo: IAIMessage[] = [
        {
            id: 0,
            role: 'model',
            message: 'AI 견적 챗봇입니다. 물건을 보내는 타입이 수입인가요? 수출인가요?',
            button: [
                { title: '수출', value: 'EXPORT' },
                { title: '수입', value: 'IMPORT' },
            ],
            action: () => { },
            inputType: 'none',
            inputDisabled: true,
            messageStep: 0,
        },
        {
            id: 1,
            role: 'model',
            message: '출발지를 입력해주세요',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 1,
        },
        {
            id: 2,
            role: 'model',
            message: '도착지를 입력해주세요',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 2,
        },
        {
            id: 3,
            role: 'model',
            message: '화물출고일을 선택해주세요.',
            button: [
                { title: '확정', value: true },
                { title: '미정', value: false },
            ],
            action: () => { },
            inputType: 'text',
            inputDisabled: true,
            messageStep: 3,
        },
        {
            id: 4,
            role: 'model',
            message: '출고일자를 입력해주세요. (YYYY-MM-DD 형식)',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 4,
        },
        {
            id: 5,
            role: 'model',
            message: '화물종류를 선택해주세요.',
            button: [
                { title: 'FCL', value: 'FCL' },
                { title: 'LCL', value: 'LCL' },
                { title: 'OOG', value: 'OOG' },
            ],
            action: () => { },
            inputType: 'text',
            inputDisabled: true,
            messageStep: 5,
        },
        {
            id: 6,
            role: 'model',
            message: 'HSCODE를 입력해주세요.',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: true,
            messageStep: 6,
        },
        {
            id: 7,
            role: 'model',
            message: '컨테이너를 선택해주세요.',
            button: [
                { title: '20GP', value: '20GP' },
                { title: '40GP', value: '40GP' },
                { title: '40HQ', value: '40HQ' },
                { title: '45GP', value: '45GP' },
                { title: '20RF', value: '20RF' },
                { title: '40RF', value: '40RF' },
            ],
            action: () => { },
            inputType: 'text',
            inputDisabled: true,
            messageStep: 7,
        },
        {
            id: 8,
            role: 'model',
            message: '컨테이너 무게를 입력해주세요. 단위 : kg (정수만 입력)',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 8,
        },
        {
            id: 9,
            role: 'model',
            message: '컨테이너 수량을 입력해주세요. (정수만 입력)',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 9,
        },
        {
            id: 10,
            role: 'model',
            message: '컨테이너를 더 추가할까요?',
            button: [
                { title: '예', value: '' },
                { title: '아니요', value: '', function: 'next' },
            ],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 10,
        },
        {
            id: 11,
            role: 'model',
            message: '포장정보 파일을 업로드해주세요.',
            button: [],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 11,
        },
        {
            id: 12,
            role: 'model',
            message: '해당내용으로 견적을 요청할까요?',
            button: [
                { title: '내용확인', value: '', function: 'confirm' },
                { title: '견적요청', value: '', function: 'save' },
            ],
            action: () => { },
            inputType: 'text',
            inputDisabled: false,
            messageStep: 12,
        },
    ]

    const init = () => {
        if (chatId <= 0) return
        jsonAxios.get(`chatbot/${chatId}/messages`).then(res => {
            // console.log(res.data)
            if (res.data.code === 200) {
                const data = res.data.data
                const quoteData: IQuote = JSON.parse(res.data.messageInfo)
                setQuoteInfo(quoteData)
                if (res.data.quoteId) {
                    setIsDone(true)
                    setQuoteId(res.data.quoteId)
                }

                if (data.length > 0) {
                    const parsedData = data.map((item: any) => ({
                        ...item,
                        button: typeof item.buttonInfo === 'string'
                            ? JSON.parse(item.buttonInfo)
                            : item.buttonInfo,
                    }));
                    parsedData[0] = {
                        ...parsedData[0],
                        button: [
                            { title: '수출', value: 'EXPORT' },
                            { title: '수입', value: 'IMPORT' },
                        ]
                    };
                    setMessages(parsedData)
                    setStep(data[data.length - 1].messageStep)

                    const containerData: IContainerList | string = res.data.containerInfo ? JSON.parse(res.data.containerInfo) : {} as IContainerList

                    if (containerData === 'DELETE') {
                        setContainerInfo({} as IContainerList)
                    }
                    else if (typeof containerData !== 'string') {
                        containerData.containerType && setContainerInfo(containerData as IContainerList)
                    }
                }
            }
        })
    }

    const start = () => {
        setStep(0)
        setContainerInfo({} as IContainerList)
        setQuoteInfo({} as IQuote)
        setMessages([stepInfo[0]])
    }

    useEffect(() => {
        scrollBottom();
    }, [messages])

    const send = () => {
        if (!context) return

        const newMessages = [{
            role: 'user',
            message: context,
            messageStep: step
        }];
        setMessages((p: any) => [...p, ...newMessages]);

        if (step === 1) {
            getPort(1);
            postReply(messages.length, context, step, quoteInfo, 'user')
        }
        else if (step === 2) {
            getPort(2);
            postReply(messages.length, context, step, quoteInfo, 'user')
        }
        // 출고일입력
        else if (step === 4) {
            if (isValidDateFormat(context)) {
                const inputDate = new Date(context); // YYYY-MM-DD → Date
                const today = new Date();

                // 시간 비교를 위해 시/분/초 제거
                today.setHours(0, 0, 0, 0);
                inputDate.setHours(0, 0, 0, 0);

                if (inputDate < today) {
                    postReply(messages.length, context, step, quoteInfo, 'user')
                    const newMessages = [
                        {
                            role: 'model',
                            message: '오늘이전의 날짜는 설정할 수 없습니다.',
                            messageStep: 4
                        },
                    ];
                    setMessages((p: any) => [...p, ...newMessages]);
                    setTimeout(() => {
                        postReply(messages.length + 1, '오늘이전의 날짜는 설정할 수 없습니다.', 4, quoteInfo, 'model')
                    }, 200)
                }
                else {
                    const temp = quoteInfo
                    temp.shippingDate = context
                    setQuoteInfo(temp)
                    postReply(messages.length, context, step, quoteInfo, 'user')
                    const newMessages = [
                        {
                            role: 'model',
                            message: '화물종류를 선택해주세요.',
                            button: [
                                { title: 'FCL', value: 'FCL' },
                                { title: 'LCL', value: 'LCL' },
                                { title: 'OOG', value: 'OOG' },
                            ],
                            messageStep: 5
                        },
                    ];
                    setMessages((p: any) => [...p, ...newMessages]);
                    setStep(p => p + 1)
                    setTimeout(() => {
                        postReply(messages.length + 1, '화물종류를 선택해주세요.', 5, temp, 'model', [
                            { title: 'FCL', value: 'FCL' },
                            { title: 'LCL', value: 'LCL' },
                            { title: 'OOG', value: 'OOG' },
                        ])
                    }, 200)
                }
            }
            else {
                postReply(messages.length, context, step, quoteInfo, 'user')
                const newMessages = [
                    {
                        role: 'model',
                        message: '올바르지 않는 형식입니다. 출고일자를 입력해주세요. (YYYY-MM-DD 형식 ex) 2026-01-01)',
                        messageStep: 4
                    },
                ];
                setMessages((p: any) => [...p, ...newMessages]);
                setTimeout(() => {
                    postReply(messages.length + 1, '올바르지 않는 형식입니다. 출고일자를 입력해주세요. (YYYY-MM-DD 형식 ex) 2026-01-01)', 4, quoteInfo, 'model')
                }, 200)
            }
        }

        // 컨테이너 무게 입력
        else if (step === 8) {
            if (isPositiveInteger(context)) {
                setContainerInfo(p => {
                    const updated = {
                        ...p,
                        containerWeight: context
                    }

                    postReply(messages.length, context, step, quoteInfo, 'user', undefined, updated).then(() => {
                        setStep(p => p + 1)
                        setTimeout(() => {
                            postReply(messages.length + 1, stepInfo[9].message, 9, quoteInfo, 'model')
                        }, 200)
                        setMessages((p: any) => [...p, stepInfo[9]]);
                    })
                    return updated
                })
            }
            else {
                postReply(messages.length, context, step, quoteInfo, 'user').then(() => {
                    const newMessages = [
                        {
                            role: 'model',
                            message: '올바르지 않는 숫자입니다. 다시 무게를 입력해주세요. 단위 : kg (정수만 입력)',
                            messageStep: 8
                        },
                    ];
                    setMessages((p: any) => [...p, ...newMessages]);
                    setTimeout(() => {
                        postReply(messages.length + 1, '올바르지 않는 숫자입니다. 다시 무게를 입력해주세요. 단위 : kg (정수만 입력)', 8, quoteInfo, 'model')
                    }, 200)
                })
            }
        }

        // 컨테이너 수량 입력
        else if (step === 9) {
            if (isPositiveInteger(context)) {
                setContainerInfo(p => {
                    const updated = {
                        ...p,
                        containerQuantity: Number(context)
                    }

                    postReply(messages.length, context, step, quoteInfo, 'user', undefined, updated).then(() => {
                        setStep(p => p + 1)
                        setTimeout(() => {
                            postReply(messages.length + 1, stepInfo[10].message, 10, quoteInfo, 'model', stepInfo[10].button)
                        }, 200)
                        setMessages((p: any) => [...p, stepInfo[10]]);
                    })
                    return updated
                })
            }
            else {
                postReply(messages.length, context, step, quoteInfo, 'user').then(() => {
                    const newMessages = [
                        {
                            role: 'model',
                            message: '올바르지 않는 숫자입니다. 다시 수량을 입력해주세요. (정수만 입력)',
                            messageStep: 9
                        },
                    ];
                    setMessages((p: any) => [...p, ...newMessages]);
                    setTimeout(() => {
                        postReply(messages.length + 1, '올바르지 않는 숫자입니다. 다시 수량을 입력해주세요. (정수만 입력)', 9, quoteInfo, 'model')
                    }, 200)
                })
            }
        }

        setContext('')
    }

    const next = () => {
        setQuoteInfo(p => {
            const isEmpty = Object.keys(containerInfo).length === 0;
            const newList = isEmpty
                ? [...(p.step2?.containerList ?? [])]
                : [...(p.step2?.containerList ?? []), containerInfo];

            const updated = {
                ...p,
                step2: {
                    ...p.step2,
                    containerList: newList
                }
            }

            postReply(messages.length, '아니요', 10, updated, 'user', undefined, 'DELETE')
            setContainerInfo({} as IContainerList)

            const newMessages = [
                {
                    role: 'user',
                    message: '아니요',
                    messageStep: 10
                },
                stepInfo[12]
            ];
            setTimeout(() => {
                postReply(messages.length + 1, stepInfo[12].message, 12, updated, 'model', stepInfo[12].button, 'DELETE')
            }, 200)
            setMessages((p: any) => [...p, ...newMessages]);
            setStep(12)

            return updated
        });
    }

    const prev = () => {
        // console.log('step >>>', step)
        setMessages(p => {
            if (p.length < 2) return p; // 안전 체크

            const last = p[p.length - 1];
            const secondLast = p[p.length - 2];

            // 삭제될 메시지 ID 저장
            const deletedIds = [secondLast.id, last.id];
            // console.log("삭제될 메시지 ID:", deletedIds);

            // step 비교
            if (last.messageStep !== secondLast.messageStep && step > 0) {
                console.log('prev')
                setStep(p => {
                    if (p === 1) {
                        setIsDelete(true);
                    }
                    else if (p === 6) {
                        setQuoteInfo(p => ({
                            ...p,
                            step2: {
                                ...p?.step2,
                                hsCode: ''
                            }
                        }))
                    }
                    else if (p === 7) {
                        const list = quoteInfo?.step2?.containerList || [];
                        if (list.length > 0) {
                            const last = list[list.length - 1];

                            setContainerInfo(last);
                            setQuoteInfo(p => ({
                                ...p,
                                step2: {
                                    ...p?.step2,
                                    containerList: p?.step2?.containerList.slice(0, -1)
                                }
                            }));

                            deleteMessage(deletedIds, last)
                            return 10;
                        }
                        else {
                            setContainerInfo({} as IContainerList);
                            setHsCodeResult('');
                            setQuoteInfo(p => ({
                                ...p,
                                step2: {
                                    ...p?.step2,
                                    hsCode: ''
                                }
                            }))
                        }
                    }
                    else if (p === 11) {
                        setHsCodeResult('');
                        setQuoteInfo(p => ({
                            ...p,
                            step2: {
                                ...p?.step2,
                                hsCode: ''
                            }
                        }))
                        deleteMessage(deletedIds)
                        return 6;
                    }
                    else if (p === 12) {
                        if (quoteInfo?.step2?.shipmentMode === 'FCL') {
                            const list = quoteInfo?.step2?.containerList || [];
                            if (list.length > 0) {
                                const last = list[list.length - 1];

                                setContainerInfo(last);
                                setQuoteInfo(p => ({
                                    ...p,
                                    step2: {
                                        ...p?.step2,
                                        containerList: p?.step2?.containerList.slice(0, -1)
                                    }
                                }));

                                deleteMessage(deletedIds, last)
                                return 10;
                            }
                        }
                        else {
                            deleteMessage(deletedIds)
                            return 11;
                        }
                    }

                    deleteMessage(deletedIds)
                    return p === 5
                        ? (!quoteInfo.isShippingDate ? p - 2 : p - 1)
                        : p - 1;
                });
            }

            // messages에서 마지막 2개 삭제
            return p.slice(0, p.length - 2);
        });
    }

    const deleteMessage = (idList: number[], containerInfo?: IContainerList) => {
        if (!Array.isArray(idList)) return

        const reqData = {
            messageIds: idList,
            ...(containerInfo ? { containerInfo: JSON.stringify(containerInfo) } : {})
        };
        jsonAxios.delete('chatbot/message/delete', { data: reqData })
    }

    const septButton = (title: string, value: string | boolean, code?: string, address?: string) => {
        // 버튼 누를시 해당 답변 유저 메세지 발송
        const newMessages = [{
            role: 'user',
            message: title,
            messageStep: step,
        }];
        setMessages((p: any) => [...p, ...newMessages]);
        // 인풋 초기화
        setContext('')
        // console.log('step >>>', step)

        // 수입, 수출버튼 클릭
        if (step === 0) {
            setMessages(p => [...p, stepInfo[step + 1]])
            setQuoteInfo((p) => {
                const updated = {
                    ...p,
                    step1: {
                        ...p?.step1,
                        tradeType: value as string,
                        transportType: 'SEA'
                    }
                };
                if (!isDelete && chatId <= 0) {
                    startChat(title, value as string)
                }
                else {
                    postReply(messages.length, title, step, updated, 'user').then(() => {
                        setTimeout(() => {
                            postReply(messages.length + 1, stepInfo[step + 1].message, stepInfo[step + 1].messageStep, updated, 'model', stepInfo[step + 1].button);
                        }, 200);
                    });
                }
                return updated;
            });
            setStep(p => p + 1)
        }
        // 출발지버튼 클릭
        else if (step === 1) {
            setMessages(p => [...p, stepInfo[step + 1]])
            setStep(p => p + 1)
            setQuoteInfo((p) => {
                const updated = {
                    ...p,
                    step1: {
                        ...p.step1,
                        originId: value as string,
                        originCode: (code as string).trim(),
                        originAddress: address as string,
                    }
                };

                postReply(messages.length, title, step, updated, 'user').then(() => {
                    setTimeout(() => {
                        postReply(messages.length + 1, stepInfo[step + 1].message, stepInfo[step + 1].messageStep, updated, 'model', stepInfo[step + 1].button);
                    }, 200);
                });

                return updated;
            });
        }
        // 도착지버튼 클릭
        else if (step === 2) {
            setMessages(p => [...p, stepInfo[step + 1]])
            setStep((p) => p + 1);
            setQuoteInfo((p) => {
                const updated = {
                    ...p,
                    step1: {
                        ...p.step1,
                        destinationId: value as string,
                        destinationCode: (code as string).trim(),
                        destinationAddress: address as string,
                    }
                };

                postReply(messages.length, title, step, updated, 'user').then(() => {
                    setTimeout(() => {
                        postReply(messages.length + 1, stepInfo[step + 1].message, stepInfo[step + 1].messageStep, updated, 'model', stepInfo[step + 1].button);
                    }, 200);
                });
                return updated;
            });
        }
        // 화물 출고일 클릭후
        else if (step === 3) {
            setMessages(p => [
                ...p,
                value as boolean ? stepInfo[step + 1] : stepInfo[step + 2]
            ])

            setQuoteInfo((p) => {
                const updated = {
                    ...p,
                    isShippingDate: value as boolean
                };

                // 출고일 확정
                if (value as boolean) {
                    setStep((p) => p + 1);
                    postReply(messages.length, title, step, updated, 'user').then(() => {
                        setTimeout(() => {
                            postReply(messages.length + 1, stepInfo[step + 1].message, stepInfo[step + 1].messageStep, updated, 'model', stepInfo[step + 1].button);
                        }, 200);
                    });
                }
                else {
                    setStep((p) => p + 2);
                    postReply(messages.length, title, step, updated, 'user').then(() => {
                        setTimeout(() => {
                            postReply(messages.length + 1, stepInfo[step + 2].message, stepInfo[step + 2].messageStep, updated, 'model', stepInfo[step + 2].button);
                        }, 200)
                    })
                }
                return updated;
            });
        }
        // 화물 종류 클릭후
        // HSCODE
        else if (step === 5) {
            setStep((p) => p + 1);
            setQuoteInfo((p) => {
                const updated = {
                    ...p,
                    step2: {
                        ...p.step2,
                        shipmentMode: value as string
                    }
                };

                postReply(messages.length, title, step, updated, 'user').then(() => {
                    const newMessages = [
                        {
                            role: 'model',
                            message: 'HSCODE를 입력해주세요.',
                            messageStep: 6
                        },
                    ];

                    setMessages((p: any) => [...p, ...newMessages]);
                    setTimeout(() => {
                        postReply(messages.length + 1, stepInfo[step + 1].message, stepInfo[step + 1].messageStep, updated, 'model');
                    }, 200);
                });
                return updated;
            });
        }
        // 컨테이너 종류 선택
        else if (step === 7) {
            setStep((p) => p + 1);

            setContainerInfo(p => {
                const updated = {
                    ...p,
                    containerType: value as string
                }

                postReply(messages.length, title, 7, quoteInfo, 'user').then(() => {
                    setMessages((p: any) => [...p, stepInfo[8]]);
                    setTimeout(() => {
                        postReply(messages.length + 1, stepInfo[8].message, 8, quoteInfo, 'model', undefined, updated)
                    }, 200)
                })
                return updated
            })
        }

        // 컨테이너 더 추가 선택
        else if (step === 10) {
            setStep(7)
            setQuoteInfo(p => {
                const isEmpty = Object.keys(containerInfo).length === 0;
                const newList = isEmpty
                    ? [...(p.step2?.containerList ?? [])]
                    : [...(p.step2?.containerList ?? []), containerInfo];

                const updated = {
                    ...p,
                    step2: {
                        ...p.step2,
                        containerList: newList
                    }
                }
                postReply(messages.length, title, 10, updated, 'user')

                setContainerInfo({} as IContainerList)
                setMessages((p: any) => [...p, stepInfo[7]]);
                setTimeout(() => {
                    postReply(messages.length + 1, stepInfo[7].message, 7, updated, 'model', stepInfo[7].button, 'DELETE')
                }, 200)
                return updated
            })
        }
    }

    const startChat = (message: string, value: string) => {
        const info = {
            step1: {
                tradeType: value
            }
        }

        const reqData = {
            userMessage: message,
            messageInfo: JSON.stringify(info)
        }
        jsonAxios.post('chatbot/start', reqData).then(res => {
            console.log(res.data)
            if (res.data.code === 200) {
                setChatId(res.data.data.roomId)
                getChatbotList();
                setMessages(p => [
                    ...p,
                    stepInfo[1]
                ])
            }
        })
    }

    const getPort = (step: number) => {
        jsonAxios.get(`/locations?locationType=SEA&search=${context}&limit=20`).then(res => {
            console.log(res.data)
            if (res.data.code === 200) {
                const portList: IPortList[] = res.data.data

                if (portList.length > 0) {
                    const buttonList: IMessageBtn[] = portList.map(item => ({
                        value: String(item.id),
                        code: `${item.countryCode ?? ''}${item.locationCode} `,
                        address: `${item.locationNameKr ?? item.locationNameEn}`,
                        title: `${item.countryCode ?? ''}${item.locationCode} | ${item.locationNameKr ?? item.locationNameEn
                            }${item.locationNameKr ? ` | ${item.locationNameEn}` : ''}`
                    }));

                    setMessages((p: any) => [
                        ...p,
                        { role: 'model', message: '', button: buttonList, messageStep: step }
                    ]);
                    postReply(messages.length + 1, '', step, quoteInfo, 'model', buttonList)
                }
                else {
                    setMessages((p: any) => [
                        ...p,
                        { role: 'model', message: `${context}에 대한 검색 결과가 없습니다. 다시 입력해주세요.`, button: [], messageStep: step }
                    ]);
                    postReply(messages.length + 1, `${context}에 대한 검색 결과가 없습니다. 다시 입력해주세요.`, step, quoteInfo, 'model')
                }
                setContext('')
            }
        })
    }

    const postReply = (
        idx: number,
        context: string,
        step: number,
        messageInfo: IQuote,
        role: string,
        buttonInfo?: IMessageBtn[],
        containerInfo?: IContainerList | 'DELETE',
        quoteId?: number,
    ): Promise<void> => {

        const reqData = {
            message: context,
            messageStep: step,
            messageInfo: JSON.stringify(messageInfo),
            role,
            buttonInfo: JSON.stringify(buttonInfo),
            containerInfo: JSON.stringify(containerInfo),
            quoteId
        };

        // console.log('idx >>', idx)
        // console.log('reqData >>', reqData)

        // 🔥 Promise 반환
        return new Promise((resolve, reject) => {
            jsonAxios.post(`chatbot/${chatId}/reply`, reqData)
                .then(res => {
                    if (res.data.code === 200) {

                        // idx가 0일 수도 있으므로 → null 체크가 정확해야 함
                        if (idx !== undefined && idx !== null) {
                            setMessages(p => {
                                const updated = [...p];
                                if (updated[idx]) {
                                    updated[idx] = {
                                        ...updated[idx],
                                        id: res.data.data.messageId
                                    };
                                }
                                return updated;
                            });
                        }

                        resolve();  // 👈 성공했으니 다음으로 진행
                    } else {
                        reject("SERVER ERROR");
                    }
                })
                .catch(err => reject(err));
        });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
        }
    };

    const handleKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const scrollBottom = () => {
        if (chatRef.current) {
            lastBotMessageRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const onScroll = () => {
        if (chatRef.current) {
            const { scrollTop } = chatRef.current;
            // if (scrollTop === 0 && isMore) {
            //     setIsTop(true)
            // }
        }
    };

    const MessageComponent = ({ m, isLastBot, lastBotMessageRef }: any) => {
        const [typedCompleted, setTypedCompleted] = useState(!m.isNew);

        const handleComplete = () => {
            setTypedCompleted(true);

            // isNew 제거
            setMessages((p: any) =>
                p.map((msg: any) =>
                    msg === m ? { ...msg, isNew: false } : msg
                )
            );

            // 스크롤 맨 아래
            chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
        };

        return (
            <div
                ref={isLastBot ? lastBotMessageRef : null}
                className={`text-lg ${m.role === "user" ? "bg-primary text-white rounded-4xl" : "bg-transparent text-white"} rounded p-4 max-w-9/10`}
                style={{ whiteSpace: "pre-wrap" }}
            >
                {!typedCompleted && m.isNew ? (
                    <ReactTyped
                        strings={[m.message]}
                        typeSpeed={5}
                        showCursor={false}
                        onComplete={() => handleComplete()}
                        onStringTyped={() => {
                            chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
                        }}
                    />
                ) : (
                    <ReactMarkdown
                        components={{
                            code({ node, className, children, ...props }) {
                                const match = /language-(\w+)/.exec(className || "");
                                return match ? (
                                    <pre className="bg-black my-3 p-3 rounded text-white">
                                        <code className="text-sm" {...props}>
                                            {children}
                                        </code>
                                    </pre>
                                ) : (
                                    <code className="px-1 rounded text-red text-sm" {...props}>
                                        {children}
                                    </code>
                                );
                            },
                        }}
                    >
                        {m.message}
                    </ReactMarkdown>
                )}
            </div>
        );
    }

    const saveQuote = () => {
        // setOnce(true)
        console.log('quoteInfo', quoteInfo)
        const reqData = {
            isShippingDate: quoteInfo.isShippingDate,
            step1: {
                ...quoteInfo.step1,
                transportType: 'SEA',
                shippingDate: quoteInfo.shippingDate
            },
            step2: {
                shipmentMode: quoteInfo.step2.shipmentMode,
                hsCode: quoteInfo.step2.hsCode,
                containerList: quoteInfo.step2.containerList,
                packingList: quoteInfo.step2.packingList
            }
        }

        console.log('reqData', reqData)

        jsonAxios.post('quote/simple', reqData).then(res => {
            if (res.data.code === 200) {
                const { quoteId: id } = res.data.data;
                setQuoteId(id)
                const formData = new FormData();
                fileArr?.forEach(file => formData.append('files', file));
                const hasFiles = (fileArr?.length > 0);

                if (hasFiles) {
                    // setPopOpen(true);
                    formAxios.post(`quote/${id}/file`, formData, {
                        onUploadProgress: (e) => {
                            // setUploadProgress(Math.round((e.loaded * 100) / (e.total || 1)));
                        },
                    }).then(res => {
                        if (res.data.code === 200) {
                            fileArr.map((item) => {
                                postReply(messages.length, item.name, 11, quoteInfo, 'user')
                            })

                            setTimeout(() => {
                                postReply(messages.length + 1, stepInfo[12].message, 12, quoteInfo, 'model', stepInfo[12].button, 'DELETE')
                                setTimeout(() => {
                                    afterUpload(id)
                                }, 200)
                            }, 200)
                        }
                        else {
                            setOnce(false)
                        }
                    });
                } else {
                    afterUpload(id)
                }
            }
            else {
                toast.error('저장중 오류가 발생했습니다.')
                // setOnce(false)
            }
        })
    }

    const afterUpload = (id: string) => {
        setFileArr([]);
        sendTeamRoom('quoteSimple', `📦 Pulse On Ship 채팅 간편 견적 요청 ⚓️\n
신청일시: ${getKoreaDateTime()}
유저: ${user.username}
회사이름: ${user.companyInfo.companyNameKr}
구분/운송: ${quoteInfo.step1.tradeType} / SEA
HSCode: ${quoteInfo.step2.hsCode}
경로: ${countryCodeToEmoji(quoteInfo.step1.originCode.slice(0, 2))} ${quoteInfo.step1.originCode} | ${quoteInfo.step1.originAddress} > ${countryCodeToEmoji(quoteInfo.step1.destinationCode.slice(0, 2))} ${quoteInfo.step1.destinationCode} | ${quoteInfo.step1.destinationAddress}
`, id)
        setIsDone(true);
        setContext('')
        const newMessages = [
            {
                role: 'user',
                message: '견적요청',
            },
        ];
        setMessages((p: any) => [...p, ...newMessages]);
        setStep(13)
        postReply(messages.length, '견적요청', 13, quoteInfo, 'user', undefined, 'DELETE', Number(id))
        toast.success('정상적으로 요청완료되었습니다.')
        setTimeout(() => {
            init()
        }, 400)
    };

    return (
        <>
            <div
                id="chatArea"
                className="flex flex-col gap-2 px-4 h-dvh overflow-y-scroll text-black"
                ref={chatRef}
                onScroll={() => {
                    onScroll();
                }}
            >
                <div className="top-0 left-0 z-[9999] fixed bg-black">
                    <p className="text-white text-2xl">step : {step}</p>
                    <p className="text-white text-2xl">containerInfo : {JSON.stringify(containerInfo)}</p>
                    <p className="text-white text-2xl">quoteInfo : {JSON.stringify(quoteInfo)}</p>
                </div>
                {
                    messages.length > 0 ?
                        messages.map((m, idx) => {
                            const isLastBot = m.role === "user" && idx === messages.length - 2;
                            return (
                                <div
                                    className={`flex w-full ${idx === messages.length - 1 ? 'mb-10' : ''}`}
                                    key={idx}
                                >
                                    <div className={`flex w-full`}>
                                        <div className={`flex flex-col gap-3 w-full ${m.role === "user" ? "items-end" : "items-start"}`}>
                                            <MessageComponent m={m} isLastBot={isLastBot} lastBotMessageRef={lastBotMessageRef} />
                                            <span className="text-white">
                                                {m.id} <br />
                                                {m.messageStep}
                                            </span>
                                            {
                                                m.button && m.button.length > 0 &&
                                                <div className="flex gap-3 max-w-full overflow-x-auto text-white">
                                                    {
                                                        m.button.map((item, idx) => (
                                                            <CustomButton
                                                                key={idx}
                                                                className="bg-primary px-4 !min-w-20 !h-10 !text-lg shrink-0"
                                                                title={item.title}
                                                                disabled={m.messageStep !== step}
                                                                onClick={() => {
                                                                    if (item.function) {
                                                                        if (item.function === 'save') {
                                                                            saveQuote();
                                                                        }
                                                                        else if (item.function === 'next') {
                                                                            next();
                                                                        }
                                                                        else if (item.function === 'confirm') {
                                                                            setPopOpen(true)
                                                                        }
                                                                    }
                                                                    else {
                                                                        septButton(item.title, item.value, item.code, item.address)
                                                                    }
                                                                }}
                                                            />
                                                        ))
                                                    }
                                                </div>
                                            }
                                            {
                                                m.messageStep === 6 && m.role === "model" && m.message.includes("HSCODE") &&
                                                <HsCode
                                                    hsCode={quoteInfo?.step2?.hsCode ?? ''}
                                                    disabled={step > 6}
                                                    onChange={(value2) => {
                                                        // console.log('value2 >>>', value2)
                                                        setQuoteInfo((p) => {
                                                            const updated = {
                                                                ...p,
                                                                step2: {
                                                                    ...p?.step2,
                                                                    hsCode: value2
                                                                }
                                                            };

                                                            setMessages(p => {
                                                                const newIndex = p.length;
                                                                const next = [...p, {
                                                                    id: 0,
                                                                    role: 'user',
                                                                    message: value2,
                                                                    messageStep: 6
                                                                }];

                                                                postReply(newIndex, value2, 6, updated, 'user')
                                                                return next;
                                                            });

                                                            setTimeout(() => {
                                                                if (quoteInfo?.step2?.shipmentMode === 'FCL') {
                                                                    setStep(7);
                                                                    setMessages(p => {
                                                                        const newIndex = p.length;
                                                                        const next = [...p, stepInfo[7]];

                                                                        postReply(
                                                                            newIndex,
                                                                            stepInfo[7].message,
                                                                            stepInfo[7].messageStep,
                                                                            updated,
                                                                            'model',
                                                                            stepInfo[7].button
                                                                        );

                                                                        return next;
                                                                    });
                                                                }
                                                                // LCL OOG 보류
                                                                else {
                                                                    setStep(11);
                                                                    setMessages(p => {
                                                                        const newIndex = p.length;
                                                                        const newMessages = {
                                                                            id: 0,
                                                                            role: 'model',
                                                                            message: '포장정보 파일을 업로드해주세요.',
                                                                            messageStep: 11
                                                                        };

                                                                        const next = [...p, newMessages];

                                                                        // postReply(
                                                                        //     newIndex,
                                                                        //     stepInfo[11].message,
                                                                        //     stepInfo[11].messageStep,
                                                                        //     updated,
                                                                        //     'model'
                                                                        // );

                                                                        return next;
                                                                    });

                                                                    // setMessages((p: any) => [...p, ...newMessages]);
                                                                    setTimeout(() => {
                                                                        postReply(messages.length + 1, '포장정보 파일을 업로드해주세요.', 11, updated, 'model')
                                                                    }, 200)
                                                                }
                                                            })
                                                            return updated;
                                                        });
                                                    }}
                                                />
                                            }
                                            {
                                                m.messageStep === 11 && m.role === "model" && m.message.includes("포장") && !isDone &&
                                                <div className="w-full max-w-[400px]">
                                                    <DragFileMulti
                                                        description='선택 또는 드래그하여<br />PL 파일을 등록해 주세요'
                                                        onChangeFileMulti={(file) => {
                                                            if (!file) return
                                                            setFileArr((p) => {
                                                                const newArr = file.filter(
                                                                    (file) =>
                                                                        !p.some((exist) =>
                                                                            exist.name === file.name && exist.size === file.size
                                                                        )
                                                                );
                                                                return newArr;
                                                            });

                                                            const userMessages = file.map(f => ({
                                                                role: 'user',
                                                                message: f.name,
                                                                messageStep: 11
                                                            }));

                                                            const newMessages = [
                                                                ...userMessages,
                                                                stepInfo[12],
                                                            ];

                                                            setMessages((p: any) => [...p, ...newMessages]);

                                                            // setTimeout(() => {
                                                            //     postReply(messages.length + 1, stepInfo[12].message, 12, quoteInfo, 'model', stepInfo[12].button, 'DELETE')
                                                            // }, 200)
                                                            setStep(12)
                                                        }}
                                                    />
                                                    <p className='text-[#A8A9BF] text-xs'>*파일은 PDF형식의 15MB로 제한됩니다.</p>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            );
                        }) :
                        <></>
                }
            </div>
            <div className={`flex items-center gap-3 text-black w-full max-w-[800px] mx-auto px-6 rounded-full 
                ${isDone ?
                    'bg-transparent' :
                    (step === 0 ||
                        step === 3 ||
                        step === 5 ||
                        step === 7 ||
                        step === 10 ||
                        step === 12 ||
                        (step === 6 && quoteInfo?.step2?.shipmentMode !== 'FCL')) ?
                        'bg-[#EFEEEF4D]' :
                        'bg-white'}`}>
                <div className="flex flex-1 justify-center items-center py-2 h-full">
                    {
                        isDone ?
                            <div>
                                <CustomButton
                                    className="bg-primary"
                                    title={'견적요청 보기'}
                                    onClick={() => {
                                        if (quoteId > 0) {
                                            navigate(`/quote/status/detail?id=${quoteId}`)
                                        }
                                    }}
                                />
                            </div> :
                            <textarea
                                className={`w-full h-full px-3 focus:outline-none bg-transparent`}
                                style={{ resize: "none" }}
                                placeholder="채팅 견적을 진행합니다."
                                disabled={
                                    step === 0 ||
                                    step === 3 ||
                                    step === 5 ||
                                    step === 10 ||
                                    step === 12 ||
                                    (step === 6 && quoteInfo?.step2?.shipmentMode !== 'FCL')
                                }
                                onKeyDown={handleKeyDown}
                                onKeyUp={handleKeyUp}
                                name="input"
                                value={context}
                                onChange={(e) => {
                                    setContext(e.target.value)
                                }}
                            />
                    }
                    {
                        messages.length !== 1 &&
                        !isDone &&
                        <CustomButton
                            title="이전"
                            className="bg-red !min-w-20 !h-10 !text-sm"
                            onClick={prev}
                        />
                    }
                </div>
            </div>

            <HsCodeModal
                isOpen={popHsCodeOpen}
                setIsOpen={setPoHsCodepOpen}
            />

            <ConfrimChatQuote
                isOpen={popOpen}
                setIsOpen={setPopOpen}
                quoteInfo={quoteInfo}
                fileArr={fileArr}
            />
        </>
    )
}
export default Quote